import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteAiConnection,
  getAiConnection,
  getAiConnections,
  revokeAiConnection,
  revokeAllAiConnections,
} from '@/api/ai-connections';

const AI_CONNECTIONS_KEY = ['ai-connections'] as const;

export function useAiConnections({ isWaitingForNew = false }: { isWaitingForNew?: boolean } = {}) {
  return useQuery({
    queryKey: AI_CONNECTIONS_KEY,
    queryFn: getAiConnections,
    // While someone is following the setup prompt, poll so the new connection shows up on its own
    refetchInterval: isWaitingForNew ? 3000 : false,
  });
}

export function useAiConnection(id: string | undefined) {
  return useQuery({
    queryKey: [...AI_CONNECTIONS_KEY, id],
    queryFn: () => getAiConnection(id as string),
    enabled: Boolean(id),
  });
}

export function useAiConnectionActions() {
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: AI_CONNECTIONS_KEY });

  return {
    revoke: useMutation({ mutationFn: revokeAiConnection, onSuccess }),
    remove: useMutation({ mutationFn: deleteAiConnection, onSuccess }),
    revokeAll: useMutation({ mutationFn: revokeAllAiConnections, onSuccess }),
  };
}
